import { Box, Step, StepContent, StepLabel } from '@mui/material';
import EditNote from '@mui/icons-material/EditNote';
import { HtmlField } from 'framework/ui/field';
import { ReplaceUrlWithLink } from 'framework/utils';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import { PersonTimelineRecordActions } from 'domain/shared/timeline/person';
import MenuItem from '@mui/material/MenuItem';
import {
  getPersonTimelineIconSx,
  personTimelineActionNameSx,
  personTimelineDateSx,
  personTimelineHeaderSx,
  personTimelineStepContentSx,
} from 'domain/shared/timeline/person/PersonTimelineRecord/personTimelineRecordSx';
import UpdateStaffingNoteForm from './UpdateStaffingNoteForm';
import { StaffingNoteItem } from 'domain/shared/staffing';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { ShowDate } from 'framework/ui/ui-builder';
import { DATE_FORMAT } from 'framework/utils';
import { timelineTextContainerSx } from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/baseTimelineRecordSx';

interface NotesListProps {
  items: StaffingNoteItem[];
  editingNoteId: string | null;
  setEditingNoteId: (noteId: string) => void;
  onCancelEdit: () => void;
  onUpdateNote: (id: string, values: any) => Promise<object>;
}

const NotesList = ({ items, editingNoteId, setEditingNoteId, onCancelEdit, onUpdateNote }: NotesListProps) => {
  const renderNoteHeader = (item: StaffingNoteItem) => (
    <Box sx={personTimelineHeaderSx}>
      <Box sx={personTimelineActionNameSx}>
        <EmployeeLinkFieldWithNameLoading value={item?.author} isOpenInNewTab />
      </Box>
      <Box sx={personTimelineDateSx}>
        <ShowDate dateUTC={item?.dateTime} format={DATE_FORMAT.FULL} />
      </Box>
    </Box>
  );

  const renderUpdateForm = (item: StaffingNoteItem) => {
    return (
      <UpdateStaffingNoteForm
        id={item.id}
        onCancel={onCancelEdit}
        updateFormFunc={onUpdateNote}
        getDataFunc={() => Promise.resolve(item)}
        afterSubmit={{
          message: 'Note has been updated successfully',
        }}
      />
    );
  };

  const renderContent = (item: StaffingNoteItem) => {
    return (
      <>
        <Box sx={timelineTextContainerSx}>
          <ReplaceUrlWithLink>
            <HtmlField value={item.noteText} />
          </ReplaceUrlWithLink>
        </Box>
        {item.attachmentPaths && item.attachmentPaths.length > 0 && (
          <Box mt={1}>
            <AttachmentsListField value={item.attachmentPaths} isStaffingService={true} />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box pt={1}>
      {items.map(item => {
        if (!item) return null;

        const isEditing = editingNoteId === item.id;

        return (
          <Step sx={{ width: '100%' }} key={`note-${item.id}`} active>
            <StepLabel
              sx={{ padding: 0 }}
              icon={
                <Box sx={getPersonTimelineIconSx()}>
                  <EditNote />
                </Box>
              }
            >
              <Box
                sx={{
                  backgroundColor: 'background.primary',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                {renderNoteHeader(item)}
                <PersonTimelineRecordActions
                  buttons={[
                    <MenuItem key="edit" onClick={() => setEditingNoteId(item.id)}>
                      Edit note
                    </MenuItem>,
                  ]}
                  showLinkButton={false}
                  timelineId={item.id}
                />
              </Box>
            </StepLabel>
            <StepContent sx={personTimelineStepContentSx}>
              {isEditing ? renderUpdateForm(item) : renderContent(item)}
            </StepContent>
          </Step>
        );
      })}
    </Box>
  );
};

export default NotesList;
